/* Хвост заказа движений (§20.2, §16; общий OrderTail — BotRun прототипа): рамка в пропорциях ролика со снимком того, кто повторит,
   «Создаём видео · Можно уйти с экрана…», этапы и деньги — из заказа сервера; отказ проверки — «Заменить видео или фото»;
   итог — ролик в рамке ролика (авторизованный openResult), «Видео готово» с подписью по герою и пресету, карточка заказа,
   «Скачать» (MP4) и «Ещё вариант», «Что дальше» — «Сделать кружок» (если сервер публикует маршрут), главное — «Открыть в чате».
   Знак над меню — вид dance или swap. */
import { useEffect, useRef, useState } from 'react';
import { uiText } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { OrderTail, type TailAction } from '../studio/circle/OrderTail';
import { tailDeliver, tailPhase } from '../studio/circle/order-tail-model';
import { useTailRetry } from '../studio/circle/tool-helpers';
import { motionCardText, motionDoneText, motionPolicy, type MotionSnapshot } from './motion-model';
import { motionCircleAppId, motionToCircle } from './motion-result-target';

export function MotionTail({
  state,
  snapshot,
  picture,
  resultActions,
}: {
  state: CircleFormState;
  snapshot: MotionSnapshot;
  /** Снимок того, кто повторит (локальное превью или картинка каталога); нет — пустая рамка */
  picture: string | null;
  resultActions: unknown;
}) {
  const retry = useTailRetry(state);
  const [error, setError] = useState('');
  const [moving, setMoving] = useState(false);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);
  const { order, result } = state;
  const phase = tailPhase(order);
  const asset = order?.result?.assets.find(a => a.mime === 'video/mp4') ?? order?.result?.assets[0];
  /* Ролик результата — в рамке: читаем файл один раз, как только сервер его отдал */
  const opened = useRef<string>('');
  useEffect(() => {
    if (phase !== 'done' || !order?.result?.available || !asset || result || state.busy) return;
    const key = `${state.orderId}:${order.result_revision}:${asset.index}`;
    if (opened.current === key) return;
    opened.current = key;
    void state.openResult(asset.index);
  }, [phase, order, asset, result, state]);
  const width = snapshot.width || 9,
    height = snapshot.height || 16;
  const ratio = width / height;
  const policy = state.form ? motionPolicy(state.form) : null;
  const audio = policy?.original_audio_preserved === true && asset?.has_audio !== false;
  const delivered = tailDeliver(order) !== 'on_request';
  const circleId = motionCircleAppId(resultActions);
  const hasAudio = !!order?.result?.assets.some(a => a.mime === 'video/mp4' && a.has_audio === true);
  const next: TailAction[] = circleId
    ? [
        {
          label: 'Сделать кружок',
          icon: 'circle',
          disabled: moving || !hasAudio,
          onClick: async () => {
            if (request.current) return;
            const controller = new AbortController();
            request.current = controller;
            setMoving(true);
            setError('');
            const timer = setTimeout(() => controller.abort(), 30000);
            try {
              await motionToCircle({
                owner: state.intent?.owner ?? state.ownerId,
                orderId: state.orderId!,
                revision: order?.result_revision,
                resultActions,
                signal: controller.signal,
              });
            } catch {
              if (request.current === controller) setError('Не удалось передать видео. Откройте исходный заказ и попробуйте снова.');
            } finally {
              clearTimeout(timer);
              if (request.current === controller) {
                request.current = null;
                setMoving(false);
              }
            }
          },
        },
      ]
    : [];
  const pic = picture ? <img src={picture} alt="" /> : undefined;
  const video =
    result && result.mime.startsWith('video/') ? (
      <div className="pr-vid motion-result">
        <video
          className="pr-vf"
          controls
          playsInline
          src={result.url}
          style={{ aspectRatio: `${width} / ${height}` }}
          aria-label={uiText('Готовое видео')}
        />
      </div>
    ) : undefined;
  return (
    <>
      <OrderTail
        state={state}
        kind={snapshot.preset}
        className="dn-ar motion-tail"
        hero={{ shape: 'frame', ratio, picture: pic, result: video }}
        texts={{
          runH: 'Создаём видео',
          runP: 'Можно уйти с экрана: бот пришлёт готовое видео в чат.',
          doneH: 'Видео готово',
          doneP: motionDoneText(snapshot, audio, delivered),
          modH: 'Видео или фото не прошли проверку',
          modWhy: 'Чужие фото без согласия и запрещённое не пропускаем.',
          notMade: 'видео не создано',
          techH: 'Не получилось собрать видео',
        }}
        card={
          motionCardText(snapshot) ? (
            <div className="ar-q">
              <small>{motionCardText(snapshot)}</small>
            </div>
          ) : undefined
        }
        next={next}
        modAction="Заменить видео или фото"
        onRetry={retry}
      />
      {circleId && phase === 'done' && !hasAudio && (
        <p className="hintp" role="status">
          {uiText('Кружок из этого видео пока недоступен: в результате нет подтверждённой звуковой дорожки.')}
        </p>
      )}
      {error && <p role="alert">{uiText(error)}</p>}
    </>
  );
}
